import { Component } from '@theme/component';
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  updateAllHeaderCustomProperties,
  updateHeaderGroupHeight,
  headerMenuDesktopQuery,
} from '@theme/utilities';
import {
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

// Debounces [data-header-hovering] removal so a fast mouse in/out doesn't yank the
// transparent-to-solid reveal back and forth.
const HEADER_HOVER_LEAVE_DEBOUNCE_MS = 200;

// Shared with sections/header.liquid's [data-header-mode='standard'] mobile grid
// overrides (headerMenuDesktopQuery, 1024px). Below it, .header__column is display:contents
// (drawer mode), so those CSS overrides alone reposition the logo regardless of DOM order -
// moving items in the DOM there too would just needlessly reorder them ahead of the
// drawer/hamburger trigger (also in the "left" column) and throw off ITS position instead,
// since it has no explicit grid-column of its own to fall back on.
const EXTENDED_HEADER_DESKTOP_QUERY = headerMenuDesktopQuery;

// How long scroll-driven header updates are suppressed after the mobile menu drawer
// releases its scroll-lock (see #ignoreScrollUntil) - long enough to cover the layout
// settling after <html overflow: hidden> is removed, short enough that a real scroll
// right after closing the drawer still responds promptly.
const DRAWER_SCROLL_LOCK_RELEASE_GUARD_MS = 150;

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'.
   * A one-way latch: the IntersectionObserver below only ever flips it true. It's only
   * ever reset to false by #updateScrollState's own isAtTop check, so it can't be undone
   * by a stale/delayed IntersectionObserver callback re-reading dataset.stickyState after
   * the header has already moved on to a different state (see #updateScrollState).
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * A `performance.now()` timestamp until which scroll-driven header updates are
   * suppressed, set by #handleDrawerToggle right as the mobile menu drawer closes and
   * releases its scroll-lock (<html overflow: hidden>, see header-drawer.liquid). Releasing
   * that lock can produce a spurious/stale scroll reading on mobile (observed as the
   * extended header's fade-out/fade-in collapse sequence briefly restarting on its own,
   * flashing the header out then back in) before layout has settled back onto the page's
   * real scroll position - a short guard window here is cheaper and more reliable than
   * trying to distinguish a genuine scroll from a spurious one after the fact.
   * @type {number}
   */
  #ignoreScrollUntil = -Infinity;

  /**
   * Bumped every time the extended header's collapse-to-standard sequence should
   * re-evaluate (scroll direction changed). In-flight timeouts from a prior sequence
   * compare their captured value against this one to detect they're stale and bail out,
   * instead of finishing a transition the user has since scrolled past.
   * @type {number}
   */
  #extendedHeaderGeneration = 0;

  /**
   * Whether an extended header has fully collapsed into looking like a standard header.
   * @type {boolean}
   */
  #isStandardHeaderMode = false;

  /**
   * The mode a collapse/restore sequence currently in flight is heading toward, or null when
   * idle. Lets #syncExtendedHeaderMode tell "already on my way there" apart from "need to
   * start a new sequence" - see the comment there for why that distinction matters.
   * @type {'standard' | 'extended' | null}
   */
  #collapsingToward = null;

  /**
   * Count of in-flight .header__row--bottom/.header-logo__images height transitions (each
   * triggered by [data-header-mode] flipping, see #collapseToStandardHeader/
   * #restoreExtendedHeader). #resizeObserver below fires on every intermediate frame of that
   * transition - suppressing its write while this is above zero avoids piling a body-wide
   * custom property write (and whatever it cascades into) on top of the transition's own
   * reflow, for a value #refreshHeaderCustomProperties already re-syncs correctly once things
   * settle. A count rather than a boolean: a superseded generation's own end-of-transition
   * timeout still fires and must still decrement its own increment, but must never clear a
   * newer, still-running sequence's suppression just because its generation is stale.
   * @type {number}
   */
  #headerHeightAnimationLocks = 0;

  /**
   * Pending removal of [data-header-hovering], while debouncing the leave side of hover.
   * @type {number | null}
   */
  #hoverLeaveTimeout = null;

  /**
   * Marks the header as hovered immediately, and cancels any pending debounced removal
   * from a just-preceding pointerleave.
   *
   * Filtered to real mice: a tap on a touch device (e.g. the mobile menu trigger, which
   * lives inside this header) fires a pointerenter too, but touch has no corresponding
   * pointerleave until some later, unrelated tap - left unfiltered, that permanently
   * latches the header solid (never transparent again) after the first tap anywhere
   * inside it.
   * @param {PointerEvent} event
   */
  #handleHeaderMouseEnter = (event) => {
    if (event.pointerType !== 'mouse') return;

    if (this.#hoverLeaveTimeout !== null) {
      clearTimeout(this.#hoverLeaveTimeout);
      this.#hoverLeaveTimeout = null;
    }
    this.dataset.headerHovering = '';
  };

  /**
   * Debounces clearing the hovered state, so a fast mouse in/out doesn't immediately
   * reverse the transparent-to-solid reveal transition on every crossing.
   * @param {PointerEvent} event
   */
  #handleHeaderMouseLeave = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#hoverLeaveTimeout = window.setTimeout(() => {
      this.#hoverLeaveTimeout = null;
      delete this.dataset.headerHovering;
    }, HEADER_HOVER_LEAVE_DEBOUNCE_MS);
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;
    if (this.#headerHeightAnimationLocks > 0) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else if (!isIntersecting) {
        // Only ever latch true here. Once sticky-active, the header is pinned back into
        // view (position: sticky), so it re-intersects and this callback fires again with
        // isIntersecting true - but by the time this async callback runs, dataset.stickyState
        // may have already moved on (e.g. rapid scroll reversals), so reading it here to
        // decide whether to un-latch is racy. #updateScrollState's isAtTop check is the only
        // place that resets this to false, driven by a synchronous scrollTop/rect read.
        this.#offscreen = true;
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');
  };

  /**
   * Starts the post-close guard window (see #ignoreScrollUntil) as soon as the mobile menu
   * drawer's `<details scroll-lock>` (header-drawer.liquid) closes and releases the shared
   * `<html>` scroll-lock. Bound on `document` with `capture: true`, mirroring dialog.js's own
   * scroll-lock listener, since the native `toggle` event doesn't bubble.
   * @param {Event} event
   */
  #handleDrawerToggle = (event) => {
    if (!(event.target instanceof HTMLDetailsElement)) return;
    if (event.target.open || !event.target.hasAttribute('scroll-lock')) return;

    this.#ignoreScrollUntil = performance.now() + DRAWER_SCROLL_LOCK_RELEASE_GUARD_MS;
  };

  /**
   * Re-runs the standard-mode item relocation when the viewport crosses
   * EXTENDED_HEADER_DESKTOP_QUERY's breakpoint. #moveHeaderItems is a no-op below that
   * breakpoint (see its docstring), so a header that collapsed to standard mode while
   * narrow never actually relocated the logo/menu/search into the standard top-row
   * positions. Left uncorrected, growing past the breakpoint while already collapsed
   * leaves the menu sitting in .header__row--bottom, which the desktop-only
   * [data-header-mode='standard'] CSS then hides, since it assumes the relocation
   * already happened. The reverse crossing needs no handling: below the breakpoint
   * .header__column is display:contents, so CSS positions everything by grid-area
   * regardless of DOM order (see #moveHeaderItems).
   */
  #handleExtendedHeaderQueryChange = () => {
    if (!EXTENDED_HEADER_DESKTOP_QUERY.matches || !this.#isStandardHeaderMode) return;
    this.#moveHeaderItems('standard');
    updateAllHeaderCustomProperties();
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    if (performance.now() < this.#ignoreScrollUntil) return;

    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (stickyMode === 'always') {
      if (this.#timeout) {
        clearTimeout(this.#timeout);
        this.#timeout = null;
      }

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      this.#syncExtendedHeaderMode();
      return;
    }

    if (isScrollingUp) {
      // Cancel a pending hide - the user reversed direction before it fired.
      if (this.#timeout) {
        clearTimeout(this.#timeout);
        this.#timeout = null;
      }

      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      // 'down', not 'none': #syncExtendedHeaderMode only collapses an extended header on
      // 'down', so a 'scroll-up' sticky header needs a real down signal here too, same as
      // the 'always' branch above - otherwise it never collapses while being scrolled past.
      this.dataset.scrollDirection = 'down';

      // Debounce hiding: position: sticky is removed from .header-section the instant
      // stickyState stops being 'active' (sections/header.liquid), snapping the header
      // back to its natural (usually far offscreen) document position with no
      // transition. Without this delay, a quick down-tick during rapid up/down
      // scrolling yanks the header away before the user can reverse again, reading as
      // the header abruptly disappearing.
      if (this.#timeout === null) {
        this.#timeout = window.setTimeout(() => {
          this.#timeout = null;
          this.dataset.stickyState = 'idle';
        }, this.#readDurationMs('--animation-speed'));
      }
    } else {
      this.dataset.scrollDirection = 'down';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
    this.#syncExtendedHeaderMode();
  };

  /**
   * Re-evaluates the extended header's collapse-to-standard sequence after a scroll
   * direction change. Once the logo/menu-row settle animation (see the header-logo block
   * and sections/header.liquid) has had time to finish, scrolling down collapses the
   * extended header into looking exactly like a standard header (same logo/menu/search
   * positions as if the "Extended header" setting were off). It only reverts back to the
   * extended header once the user has scrolled all the way back up to the top of the page
   * (scrollDirection 'none') - merely scrolling upward ('up', still mid-page) leaves it in
   * whatever state it's already in, same as a normal sticky header would.
   */
  /**
   * Reflects "a collapse/restore sequence is in flight" ([data-header-height-transitioning],
   * sections/header.liquid) onto the element - true from the moment #syncExtendedHeaderMode
   * commits to a new #collapsingToward target until #headerHeightAnimationLocks drops back to
   * zero. Call this right after touching either field.
   *
   * Exists so the page's real scroll containers (html/.page-wrapper, assets/base.css) can
   * disable CSS scroll anchoring for exactly this window: collapsing/restoring the extended
   * header changes --header-height, which shifts the margin-top of the .shopify-section right
   * after it (sections/header.liquid) - a layout shift the browser's scroll anchoring
   * "corrects" by silently adjusting scrollTop. #updateScrollState can't tell that
   * self-inflicted adjustment apart from a real user scroll, reads it as one, and flips
   * data-scroll-direction - re-triggering the opposite sequence, which shifts layout again,
   * anchors again, flips again: a self-sustaining loop near the top of any page with an
   * extended header, confirmed live on production. Scoped to this window (rather than
   * disabling anchoring permanently) so every other legitimate use of it on the page - a late
   * image, an app embed, the announcement bar's rotating text - keeps working.
   */
  #syncScrollAnchorSuppression() {
    if (this.#collapsingToward !== null || this.#headerHeightAnimationLocks > 0) {
      this.dataset.headerHeightTransitioning = '';
    } else {
      delete this.dataset.headerHeightTransitioning;
    }
  }

  #syncExtendedHeaderMode = () => {
    if (!this.hasAttribute('data-extended-header')) return;

    const direction = this.dataset.scrollDirection;
    if (direction === 'up') {
      // Bump (without starting a new sequence) to invalidate a collapse/restore that a
      // just-preceding 'down'/'none' tick may have queued. Without this, a brief down-tick
      // during rapid up/down scrolling schedules a fade-to-standard that's still pending -
      // reversing to 'up' only skipped queuing anything *new*, it never cancelled that one,
      // so it fades the header out later regardless, well after the user already reversed.
      this.#extendedHeaderGeneration++;
      this.#collapsingToward = null;
      this.#syncScrollAnchorSuppression();
      return;
    }

    const targetMode = direction === 'down' ? 'standard' : 'extended';
    const currentMode = this.#isStandardHeaderMode ? 'standard' : 'extended';
    // Already settled in the target mode, or already mid-sequence heading there - nothing new
    // to start. Without this, a momentum scroll settling at the top fires several 'none' ticks
    // in a row, each one restarting the fade-out/relocate/fade-in sequence from scratch; the
    // earlier (now-superseded) ones' own stale-generation cleanup then clears
    // [data-header-collapsing] - removing its opacity: 0 - while the real, still-standard
    // header hasn't actually started fading yet, flashing it back to full visibility for a
    // frame before the final sequence takes over.
    if (currentMode === targetMode && this.#collapsingToward === null) return;
    if (this.#collapsingToward === targetMode) return;

    this.#collapsingToward = targetMode;
    this.#syncScrollAnchorSuppression();
    const generation = ++this.#extendedHeaderGeneration;

    if (targetMode === 'standard') {
      this.#collapseToStandardHeader(generation);
    } else {
      this.#restoreExtendedHeader(generation);
    }
  };

  /**
   * Reads a duration custom property (e.g. `--animation-speed-slow`) in milliseconds.
   * @param {string} customProperty
   * @returns {number}
   */
  #readDurationMs(customProperty) {
    const raw = getComputedStyle(this).getPropertyValue(customProperty).trim();
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return 0;
    return raw.endsWith('ms') ? value : value * 1000;
  }

  /**
   * Relocates the logo, menu, and search items between the extended header's centered/
   * bottom-row layout and this shop's standard header layout (logo + menu on the left of
   * the top row, search on the right, ahead of the cart/account actions). Hardcoded to
   * those positions rather than reading them generically from section settings - the
   * extended header can be toggled independently per template, but all templates share
   * the same underlying header section, so the "standard" positions are always the same.
   * Desktop-only: below EXTENDED_HEADER_DESKTOP_QUERY's breakpoint, the logo's mobile
   * position already comes from grid-area alone (sections/header.liquid), regardless of DOM
   * order - moving it (and the desktop-only menu) into the "left" column there would just
   * push them ahead of the drawer/hamburger trigger, which also lives in "left" and, unlike
   * the logo, has no explicit grid-column of its own to fall back on if its position in the
   * auto-placement order changes.
   *
   * Reads its column anchors via [data-header-column] (snippets/header-row.liquid) - a
   * functional hook dedicated to this relocation, kept separate from that same element's
   * data-testid so renaming the test id can't silently disable this method.
   * @param {'standard' | 'extended'} mode
   */
  #moveHeaderItems(mode) {
    if (!EXTENDED_HEADER_DESKTOP_QUERY.matches) return;

    const logo = this.querySelector('.header-logo');
    const menu = this.refs.headerMenu;
    const search = this.querySelector('.search-action:not(.search-action--hidden-on-menu)');
    if (!logo || !menu || !search) return;

    if (mode === 'standard') {
      const topLeft = this.querySelector('[data-header-column="top-left"]');
      const topRight = this.querySelector('[data-header-column="top-right"]');
      if (!topLeft || !topRight) return;

      // Logo, then menu, ahead of whatever already lives there (drawer search, if present).
      topLeft.prepend(menu);
      topLeft.prepend(logo);
      // Search joins the right column ahead of the cart/account actions.
      const actions = this.querySelector('header-actions');
      if (actions) {
        topRight.insertBefore(search, actions);
      } else {
        topRight.appendChild(search);
      }
    } else {
      const topCenter = this.querySelector('[data-header-column="top-center"]');
      const bottomCenter = this.querySelector('[data-header-column="bottom-center"]');
      const topLeft = this.querySelector('[data-header-column="top-left"]');
      if (!topCenter || !bottomCenter || !topLeft) return;

      topCenter.appendChild(logo);
      bottomCenter.appendChild(menu);
      topLeft.prepend(search);
    }
  }

  /**
   * Fades the current (extended) header out, relocates its items into the standard
   * header's positions, then fades/slides the result back in.
   * @param {number} generation
   */
  #collapseToStandardHeader(generation) {
    if (this.#isStandardHeaderMode) {
      // Already there - #syncExtendedHeaderMode may have optimistically set this before
      // calling in (e.g. a same-direction tick arriving while the opposite sequence hadn't
      // flipped #isStandardHeaderMode yet). Nothing is starting, so idle is the only correct
      // value, regardless of what it was set to on the way in.
      this.#collapsingToward = null;
      this.#syncScrollAnchorSuppression();
      return;
    }

    // Brief head start so the extended logo's own fade/slide-away (see the header-logo
    // block) and the bottom row's pull-up (sections/header.liquid) register as a distinct
    // motion before the whole header cross-fades to standard - kept short so it doesn't
    // read as a stall before anything happens.
    const settleDelay = this.#readDurationMs('--animation-speed-fast');

    window.setTimeout(() => {
      if (generation !== this.#extendedHeaderGeneration) return;

      const fadeOutDuration = this.#readDurationMs('--extended-header-collapse-speed');
      this.dataset.headerCollapsing = 'out';

      window.setTimeout(() => {
        if (generation !== this.#extendedHeaderGeneration) {
          // Invalidated mid-fade (the user reversed direction before this landed) - clear
          // the fade-out flag rather than leaving the header stuck at opacity: 0 forever.
          delete this.dataset.headerCollapsing;
          return;
        }

        this.#moveHeaderItems('standard');
        this.dataset.headerMode = 'standard';
        this.#isStandardHeaderMode = true;
        this.#collapsingToward = null;
        this.#headerHeightAnimationLocks++;
        this.#syncScrollAnchorSuppression();
        this.dataset.headerCollapsing = 'in';
        // Force a style flush before clearing [data-header-collapsing]. Without it, the
        // reparenting above and this removal can get coalesced into one recalc with no
        // "before" snapshot to transition from, so the fade/slide-in never plays (same
        // issue as portalSubmenu() in header-menu.js).
        void this.offsetHeight;
        delete this.dataset.headerCollapsing;
        this.#refreshHeaderCustomProperties(generation);
      }, fadeOutDuration);
    }, settleDelay);
  }

  /**
   * The menu drawer's top offset (header-drawer.liquid) is derived from --header-height and
   * --header-group-height. This element's own --header-height stays current on its own via
   * the ResizeObserver below, but --header-group-height and
   * --transparent-header-offset-boolean have no equivalent live observer - refresh them
   * explicitly right after collapsing/restoring, and again once the fade/slide that follows
   * has finished settling into its final layout.
   * @param {number} generation
   */
  #refreshHeaderCustomProperties(generation) {
    updateAllHeaderCustomProperties();
    const fadeInDuration = this.#readDurationMs('--extended-header-collapse-speed');
    window.setTimeout(() => {
      this.#headerHeightAnimationLocks--;
      if (generation === this.#extendedHeaderGeneration) {
        updateAllHeaderCustomProperties();
      }
      // After, not before: this is the last write --header-height can get from this
      // sequence, so scroll anchoring must stay suppressed through it too.
      this.#syncScrollAnchorSuppression();
    }, fadeInDuration);
  }

  /**
   * Reverses `#collapseToStandardHeader`: fades the standard header out, moves its items
   * back to the extended layout, then fades/slides the extended header back in.
   * @param {number} generation
   */
  #restoreExtendedHeader(generation) {
    if (!this.#isStandardHeaderMode) {
      // See the matching comment in #collapseToStandardHeader.
      this.#collapsingToward = null;
      this.#syncScrollAnchorSuppression();
      return;
    }

    // Same brief head start #collapseToStandardHeader uses before its own 'out' phase -
    // here it doubles as a debounce. Without it, this sequence used to flip
    // [data-header-collapsing]/[data-header-content-hidden] the instant scrollDirection
    // landed on 'none', so a rapid down/up/down flutter (e.g. a quick rubber-band bounce
    // at the top) restarted the fade-out every single tick, each restart visibly yanking
    // the still-standard header back to full opacity before immediately fading it again.
    // #collapseToStandardHeader's settleDelay already absorbs the same flutter on the way
    // down (a same-tick generation bump here cancels it before it ever touches the DOM);
    // this just gives the way up the same grace window.
    const settleDelay = this.#readDurationMs('--animation-speed-fast');

    window.setTimeout(() => {
      if (generation !== this.#extendedHeaderGeneration) return;

      // Held for the whole sequence below, on top of (and outlasting)
      // [data-header-mode='standard'] - see its use in the header-logo block and
      // .header__row--bottom's rules here. Without this, removing [data-header-mode]
      // partway through (right when the DOM is relocated back to extended, below) let the
      // extended logo/row's own reveal transition start in the exact same tick as
      // .header__row--top's fade/slide-in - since the logo/row sit *inside* .header__row--top,
      // their opacity stacked multiplicatively with their fading-in ancestor's, so almost all
      // of the motion stayed imperceptibly faint until the very end, reading as a jump instead
      // of an animation. Keeping them hidden until .header__row--top's own fade-in has fully
      // landed lets their own settle motion play afterward as a distinct, visible motion.
      this.dataset.headerContentHidden = 'true';

      const fadeOutDuration = this.#readDurationMs('--extended-header-collapse-speed');
      this.dataset.headerCollapsing = 'out';

      window.setTimeout(() => {
        if (generation !== this.#extendedHeaderGeneration) {
          // See the matching comment in #collapseToStandardHeader.
          delete this.dataset.headerCollapsing;
          delete this.dataset.headerContentHidden;
          return;
        }

        this.#moveHeaderItems('extended');
        delete this.dataset.headerMode;
        this.#isStandardHeaderMode = false;
        this.#collapsingToward = null;
        this.#headerHeightAnimationLocks++;
        this.#syncScrollAnchorSuppression();
        this.dataset.headerCollapsing = 'in';
        // See the matching comment in #collapseToStandardHeader.
        void this.offsetHeight;
        delete this.dataset.headerCollapsing;
        this.#refreshHeaderCustomProperties(generation);

        const rowFadeInDuration = this.#readDurationMs('--extended-header-collapse-speed');
        window.setTimeout(() => {
          if (generation !== this.#extendedHeaderGeneration) return;
          delete this.dataset.headerContentHidden;
        }, rowFadeInDuration);
      }, fadeOutDuration);
    }, settleDelay);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);

    const stickyMode = this.getAttribute('sticky');
    // The theme editor auto-scrolls the preview to whichever section is selected, firing a
    // real scroll event before the merchant does anything. That falsely triggers the sticky/
    // extended-header collapse logic below, making a transparent header render solid (wrong
    // text color) from first paint. Skip scroll tracking entirely in the editor so the header
    // always shows its configured "at rest" appearance there.
    if (stickyMode && !window.Shopify?.designMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      document.addEventListener('toggle', this.#handleDrawerToggle, { capture: true });
      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }

    // Drives [data-header-hovering] (sections/header.liquid), the transparent header's
    // hover-to-solid reveal - only relevant, so only attached, when transparent.
    if (this.hasAttribute('transparent')) {
      this.addEventListener('pointerenter', this.#handleHeaderMouseEnter);
      this.addEventListener('pointerleave', this.#handleHeaderMouseLeave);
    }

    if (this.hasAttribute('data-extended-header')) {
      EXTENDED_HEADER_DESKTOP_QUERY.addEventListener('change', this.#handleExtendedHeaderQueryChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    document.removeEventListener('toggle', this.#handleDrawerToggle, { capture: true });
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#timeout !== null) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
    this.removeEventListener('pointerenter', this.#handleHeaderMouseEnter);
    this.removeEventListener('pointerleave', this.#handleHeaderMouseLeave);
    EXTENDED_HEADER_DESKTOP_QUERY.removeEventListener('change', this.#handleExtendedHeaderQueryChange);
    if (this.#hoverLeaveTimeout !== null) {
      clearTimeout(this.#hoverLeaveTimeout);
      this.#hoverLeaveTimeout = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child. Recomputes fresh from
  // the live DOM on every delivery rather than summing `entries` directly —
  // a ResizeObserver batch only contains targets whose box actually changed
  // since the last delivery, not every observed target, so summing just the
  // batch silently drops siblings that didn't resize (e.g. the announcement
  // bar sitting still while the header alone animates through an extended-header
  // collapse/restore), leaving --header-group-height stuck too small.
  //
  // Scoped to updateHeaderGroupHeight() rather than updateAllHeaderCustomProperties():
  // this observer also watches the header element itself, and the latter unconditionally
  // writes --header-height too, bypassing HeaderComponent's own #headerHeightAnimationLocks
  // guard (its own resize observer, above) during the extended-header collapse/restore
  // animation.
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => {
      updateHeaderGroupHeight();
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
